/**
 * shipment-available-cart-check.js
 *
 * Validates pre-order items in the cart against remaining ShipmentAvailable capacity.
 * Shows an inline warning per cart line when the cart quantity meets or exceeds
 * the remaining cap. Does NOT block cart submission — the checkout extension handles
 * hard enforcement.
 *
 * Expects cart-items.liquid to render cart rows with:
 *   data-product-id="..."        — Shopify product GID (gid://shopify/Product/...)
 *   data-stock-type="backorder"  — only present on pre-order lines
 *
 * Calls POST /apps/isalbi-periscope/shipment-available with { productIds: [...] }.
 */

(function () {
  'use strict';

  const ENDPOINT =
    (window.theme && window.theme.routes && window.theme.routes.shipmentAvailable) ||
    '/apps/isalbi-periscope/shipment-available';
  const WARNING_ATTR = 'data-shipment-cap-warning';
  const CACHE_TTL_MS = 60_000; // 1 minute client-side cache

  /** @type {Map<string, { data: any[], ts: number }>} */
  const cache = new Map();

  /**
   * Fetch remaining capacity for a set of product GIDs.
   * @param {string[]} productIds
   * @returns {Promise<Array<{productId: string, sku: string|null, soh: number, shipmentAvailable: number, moq: number, eta: string|null, existingPreorders: number, remaining: number|null}>>}
   */
  async function fetchCapacity(productIds) {
    const cacheKey = productIds.slice().sort().join(',');
    const hit = cache.get(cacheKey);
    if (hit && Date.now() - hit.ts < CACHE_TTL_MS) return hit.data;

    try {
      const shop = window.Shopify?.shop;
      const url = shop ? `${ENDPOINT}?shop=${encodeURIComponent(shop)}` : ENDPOINT;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productIds }),
      });
      if (!res.ok) return [];
      const data = await res.json();
      if (!Array.isArray(data)) return [];
      cache.set(cacheKey, { data, ts: Date.now() });
      return data;
    } catch {
      return [];
    }
  }

  /**
   * Find all pre-order cart rows and their quantities.
   * @returns {Array<{row: HTMLElement, productGid: string, qty: number}>}
   */
  function getPreorderCartRows() {
    const rows = document.querySelectorAll('.cart-item[data-product-id]');
    const result = [];
    rows.forEach((row) => {
      const stockType = row.getAttribute('data-stock-type');
      if (stockType !== 'backorder') return;

      const numericId = row.getAttribute('data-product-id');
      if (!numericId) return;
      // Convert numeric ID to GID if needed
      const productGid = numericId.startsWith('gid://')
        ? numericId
        : `gid://shopify/Product/${numericId}`;

      // Read quantity from the quantity input within the row
      const qtyInput = row.querySelector('input[name="updates[]"], input[type="number"]');
      const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

      result.push({ row, productGid, qty });
    });
    return result;
  }

  /**
   * Inject or update the capacity warning element within a cart row.
   * @param {HTMLElement} row
   * @param {string|null} message - null to clear
   */
  function setRowWarning(row, message) {
    let el = row.querySelector(`[${WARNING_ATTR}]`);
    if (!message) {
      if (el) el.remove();
      return;
    }
    if (!el) {
      el = document.createElement('div');
      el.setAttribute(WARNING_ATTR, '');
      el.className = 'shipment-cap-warning text-sm mt-1';
      el.style.cssText = 'color: #b45309; background: #fef3c7; border-radius: 4px; padding: 4px 8px;';
      // Insert after the product title area
      const details = row.querySelector('.cart-item__details');
      if (details) details.appendChild(el);
      else row.appendChild(el);
    }
    el.textContent = message;
  }

  async function checkCapacity() {
    const preorderRows = getPreorderCartRows();
    if (preorderRows.length === 0) return;

    const productIds = [...new Set(preorderRows.map((r) => r.productGid))];
    const capacityData = await fetchCapacity(productIds);
    if (capacityData.length === 0) return;

    const capacityByProductId = new Map(capacityData.map((e) => [e.productId, e]));

    // FIX-13: total the cart PER PRODUCT, not per row. The same product can appear
    // on several rows (different forward-delivery dates, for example) and the cap
    // applies to its total across the cart. Warning against each row's own
    // quantity let two rows of 60 both look fine against a cap of 100.
    const qtyByProductId = new Map();
    preorderRows.forEach(({ productGid, qty }) => {
      qtyByProductId.set(productGid, (qtyByProductId.get(productGid) || 0) + qty);
    });

    preorderRows.forEach(({ row, productGid }) => {
      const cap = capacityByProductId.get(productGid);
      if (!cap) {
        setRowWarning(row, null);
        return;
      }

      // FIX-13: `remaining` is null when the product has no ETA, meaning the
      // confirmed-stock rule does not apply. Guard explicitly — a null would
      // otherwise coerce to 0 in the comparisons below and wrongly warn that
      // every non-pre-order product had no stock left.
      if (cap.remaining === null || cap.remaining === undefined) {
        setRowWarning(row, null);
        return;
      }

      // Cart total for this product, not just this row.
      const qty = qtyByProductId.get(productGid) || 0;

      // `remaining` is now confirmed stock (SOH + shipment_available, floored to a
      // MOQ multiple), matching what Pro enforces. It used to be
      // shipment_available alone, which understated the true allowance.
      const confirmedStock = (cap.soh || 0) + (cap.shipmentAvailable || 0);

      if (cap.remaining <= 0) {
        setRowWarning(row, `No pre-order stock remaining — this item cannot be ordered.`);
      } else if (qty > cap.remaining) {
        setRowWarning(
          row,
          `Only ${cap.remaining} unit${cap.remaining === 1 ? '' : 's'} available to pre-order.`
        );
      } else if (cap.remaining <= Math.ceil(confirmedStock * 0.1)) {
        // Soft advisory when within 10% of confirmed stock
        setRowWarning(
          row,
          `${cap.remaining} unit${cap.remaining === 1 ? '' : 's'} remaining to pre-order.`
        );
      } else {
        setRowWarning(row, null);
      }
    });
  }

  // Run on page load and whenever the cart updates
  function init() {
    checkCapacity();

    // Listen for Shopify cart update events fired by cart-items.js and other cart scripts
    document.addEventListener('cart:updated', checkCapacity);
    document.addEventListener('cart:refresh', checkCapacity);

    // Also observe DOM changes in case the cart section is re-rendered (section render API).
    // Guard flag prevents re-entrant calls: setRowWarning mutates the observed DOM, which would
    // otherwise re-trigger this observer endlessly.
    const cartForm = document.querySelector('form[action="/cart"]');
    if (cartForm) {
      let observerRunning = false;
      new MutationObserver(() => {
        if (observerRunning) return;
        observerRunning = true;
        checkCapacity().finally(() => {
          observerRunning = false;
        });
      }).observe(cartForm, {
        childList: true,
        subtree: true,
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
